import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@mui/styles";
import { Paper, Portal, Tooltip, Drawer } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import AIChatContent from "./AIChatContent";
import { useModel } from "hooks/useModel";
import { AuthProvider } from "pages";
import { FEATURE_KEY } from "constant";

type DockPosition = "left" | "right" | "top" | "bottom";

const useStyles = makeStyles((theme) => ({
  dockIcon: {
    position: "fixed",
    width: 50,
    height: 50,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    boxShadow: theme.shadows[6],
    zIndex: 2000, // 使用更高的 z-index 值确保在最上层
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform",
    userSelect: "none", // 防止选中
    "&:active": {
      cursor: "grabbing",
    },
    "&:hover:not(:active)": {
      transform: "scale(1.1)",
    },
    // 添加额外的样式确保在最上层
    "& *": {
      pointerEvents: "none", // 防止子元素干扰事件
    },
    // 确保在所有情况下都在最上层
    "&&": {
      zIndex: "2000 !important",
    },
  },
}));

const DockableChatIcon: React.FC = () => {
  const authDetail = useContext(AuthProvider);

  if (!authDetail?.features?.includes(FEATURE_KEY.SP_AI_ASSISTANT)) {
    return null;
  }

  const classes = useStyles();
  const [position, setPosition] = useState<DockPosition>("top");
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const { fullScreen, drawerWidth, setDrawerWidth } = useModel("aichat");
  const baseTheme = useTheme();
  const menuTheme = React.useMemo(
    () =>
      createTheme(baseTheme, {
        components: {
          MuiPopover: {
            styleOverrides: {
              root: {
                zIndex: baseTheme.zIndex.modal + 2,
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                zIndex: baseTheme.zIndex.modal + 2,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              root: {
                zIndex: baseTheme.zIndex.modal + 2,
              },
              paper: {
                zIndex: baseTheme.zIndex.modal + 2,
              },
            },
          },
        },
      }),
    [baseTheme]
  );

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // 只对左键点击响应
    if (e.button !== 0) return;

    e.preventDefault(); // 防止默认的拖拽行为
    e.stopPropagation(); // 防止事件冒泡

    setIsDragging(true);
    setHasDragged(false);

    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      // 禁用过渡效果以提高拖拽性能
      iconRef.current.style.transition = "none";
    }
  }, []);

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !iconRef.current) return;

      // 防止默认滚动行为
      e.preventDefault();

      // 标记已经拖拽过
      setHasDragged(true);

      // 取消之前的动画帧
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // 使用requestAnimationFrame优化性能
      animationFrameRef.current = requestAnimationFrame(() => {
        if (!iconRef.current) return;

        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // 获取图标尺寸和视窗尺寸
        const iconWidth = iconRef.current.offsetWidth;
        const iconHeight = iconRef.current.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // 限制图标不超出屏幕边界
        const minX = 0;
        const maxX = viewportWidth - iconWidth;
        const minY = 0;
        const maxY = viewportHeight - iconHeight;

        const constrainedX = Math.max(minX, Math.min(maxX, newX));
        const constrainedY = Math.max(minY, Math.min(maxY, newY));

        // 使用transform代替left/top以获得更好的性能，同时添加缩放效果
        iconRef.current.style.transform = `translate(${constrainedX}px, ${constrainedY}px) scale(1.05)`;
        iconRef.current.style.left = "0";
        iconRef.current.style.top = "0";
        iconRef.current.style.right = "auto";
        iconRef.current.style.bottom = "auto";
      });
    },
    [isDragging, dragOffset]
  );

  const handleDragEnd = useCallback(() => {
    // 清理动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }

    // 先检查是否正在拖拽和ref存在，再清除状态
    if (!isDragging || !iconRef.current) {
      setIsDragging(false);
      return;
    }

    // 恢复过渡效果
    iconRef.current.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const rect = iconRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const edgeThreshold = 100;
    let shouldSnap = false;
    let newPosition = position;

    // 检查是否在吸附阈值内
    if (centerX < edgeThreshold) {
      newPosition = "left";
      shouldSnap = true;
    } else if (centerX > viewportWidth - edgeThreshold) {
      newPosition = "right";
      shouldSnap = true;
    } else if (centerY < edgeThreshold) {
      newPosition = "top";
      shouldSnap = true;
    } else if (centerY > viewportHeight - edgeThreshold) {
      newPosition = "bottom";
      shouldSnap = true;
    }

    // 只有在阈值内才吸附，否则保持当前位置
    if (shouldSnap) {
      setPosition(newPosition);
      snapToEdge(newPosition);
    } else {
      // 如果不在阈值内，保持当前位置但恢复正常大小
      const currentTransform = iconRef.current.style.transform;
      if (currentTransform.includes("scale(1.05)")) {
        iconRef.current.style.transform = currentTransform.replace("scale(1.05)", "scale(1)");
      }
    }

    // 最后设置拖拽状态为false
    setIsDragging(false);
  }, [isDragging, position]);

  const snapToEdge = (edge: DockPosition) => {
    if (!iconRef.current) {
      return;
    }

    const margin = 5;

    // 重置transform，准备使用位置属性
    iconRef.current.style.transform = "";

    switch (edge) {
      case "left":
        iconRef.current.style.left = `${margin}px`;
        iconRef.current.style.top = "85%";
        iconRef.current.style.right = "auto";
        iconRef.current.style.bottom = "auto";
        iconRef.current.style.transform = "translateY(-50%)";
        break;
      case "right":
        iconRef.current.style.left = "auto";
        iconRef.current.style.top = "80%";
        iconRef.current.style.right = `${margin}px`;
        iconRef.current.style.bottom = "auto";
        iconRef.current.style.transform = "translateY(-50%)";
        break;
      case "top":
        iconRef.current.style.left = "21%";
        iconRef.current.style.top = `${margin}px`;
        iconRef.current.style.right = "auto";
        iconRef.current.style.bottom = "auto";
        iconRef.current.style.transform = "translateX(-50%)";
        break;
      case "bottom":
        iconRef.current.style.left = "50%";
        iconRef.current.style.top = "auto";
        iconRef.current.style.right = "auto";
        iconRef.current.style.bottom = `${margin}px`;
        iconRef.current.style.transform = "translateX(-50%)";
        break;
    }
  };

  useEffect(() => {
    // 在组件挂载后确保正确的初始位置
    const timer = setTimeout(() => {
      if (iconRef.current) {
        snapToEdge(position);
      }
    }, 0);

    // 清理函数
    return () => {
      clearTimeout(timer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
    };
  }, []);

  useEffect(() => {
    // 当图标重新显示时，重置到默认位置
    if (!open || !fullScreen) {
      setPosition("top");
      snapToEdge("top");
    }
  }, [open, fullScreen]);

  // 管理拖拽事件监听器
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDragMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleDragEnd, {
        passive: false,
      });

      return () => {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <>
      <Portal>
        {(!open || !fullScreen) && (
          <Tooltip
            title="How can I assist you today?"
            placement="right"
          >
            <Paper
              ref={iconRef}
              className={classes.dockIcon}
              style={{
                backgroundColor: "transparent",
                color: "transparent",
                boxShadow: isDragging ? "0 8px 32px rgba(0, 0, 0, 0.3)" : "none",
                transition: isDragging ? "none" : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: isDragging ? "grabbing" : "grab",
                // zIndex: 500, // 使用 CSS z-index 的最大值
                // 添加额外的保障
                position: "fixed",
                isolation: "isolate", // 创建新的层叠上下文
                // 移除 transform 避免与位置设置冲突
              }}
              onMouseDown={handleDragStart}
              onClick={(e) => {
                if (hasDragged) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                setOpen(true);
              }}
            >
              <img
                src="/portfolio/hover-light.gif"
                alt="AI Assistant"
                style={{
                  pointerEvents: "none", // 防止图片干扰拖拽事件
                  userSelect: "none", // 防止图片被选中
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  position: "relative", // 确保图片在容器内正确定位
                  zIndex: 1, // 确保图片显示但不干扰事件
                }}
                draggable={false} // 防止图片自身的拖拽行为
              />
            </Paper>
          </Tooltip>
        )}
      </Portal>

      <Portal>
        <ThemeProvider theme={menuTheme}>
          <Drawer
            open={open}
            disableEscapeKeyDown
            onClose={() => setOpen(false)}
            disableEnforceFocus
            hideBackdrop
            disableScrollLock
            anchor={"right"}
            ModalProps={{
              keepMounted: true,
              disablePortal: false,
              disableAutoFocus: true,
              disableRestoreFocus: true,
            }}
            PaperProps={{
              style: {
                pointerEvents: "auto",
                width: fullScreen ? "100vw" : drawerWidth,
                padding: "64px auto 0",
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "row",
              },
              onWheel: (e: React.WheelEvent) => {
                e.stopPropagation();
              },
            }}
            sx={{
              pointerEvents: "none",
              zIndex: (theme) => theme.zIndex.modal + 1,
              "& .MuiDrawer-paper": {
                pointerEvents: "auto",
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "row",
                zIndex: (theme) => theme.zIndex.modal + 1,
                height: "100vh",
                overflowY: "auto",
                overscrollBehavior: "contain",
              },
            }}
          >
            {/* 拖拽条 */}
            <div
              style={{
                width: 6,
                cursor: "ew-resize",
                background: "#e0e0e0",
                height: "100%",
                position: "relative",
                zIndex: 2,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = parseInt(drawerWidth.split("px")[0], 10);
                const minWidth = 464;
                const maxWidth = window.innerWidth * 0.9;
                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const delta = startX - moveEvent.clientX;
                  let newWidth = startWidth + delta;
                  if (newWidth < minWidth) newWidth = minWidth;
                  if (newWidth > maxWidth) newWidth = maxWidth;
                  setDrawerWidth(`${newWidth}px`);
                };
                const handleMouseUp = () => {
                  document.removeEventListener("mousemove", handleMouseMove);
                  document.removeEventListener("mouseup", handleMouseUp);
                };
                document.addEventListener("mousemove", handleMouseMove);
                document.addEventListener("mouseup", handleMouseUp);
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: 48,
                  margin: "auto",
                  background: "#bdbdbd",
                  borderRadius: 3,
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translateY(-50%)",
                  opacity: 0.7,
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
              <AIChatContent onClose={() => setOpen(false)} />
            </div>
          </Drawer>
        </ThemeProvider>
      </Portal>
    </>
  );
};

export default DockableChatIcon;
